import React, { useContext, useEffect, useState } from 'react';
import { FaArrowLeft, FaPlus, FaMapMarkerAlt, FaCheck } from 'react-icons/fa';
import { useNavigate } from 'react-router-dom';
import PhoneInput from 'react-phone-input-2';
import 'react-phone-input-2/lib/style.css';
import MultiSelect from '../../Components/utils/MultiSelect';
import Dropdown from '../../Components/utils/Dropdown';
import firstBackground from '../../images/svgs/signUp3.svg';
import secondImage from '../../images/pngs/signUp3.png';
import thirdImage from '../../images/pngs/signUp4.png';
import { showToast } from '../../Components/utils/ShowToast';
import axios from 'axios';
import countries from "../../data/countries.json"
import { states } from "../../data/constants"
import languages from "../../data/languages.json"
import { getCurrencyForCountry } from '../../data/countryToCurrency';
import { LoginContext } from '../../Contexts/LoginContext';
import SEO from '../../Components/utils/SEO';

const SignUpForm = () => {
    const [step, setStep] = useState(1);
    const [formData, setFormData] = useState({
        country: '', state: '', city: '',
        address: '', zipCode: '', fullName: '', username: '', phone: '', secondaryPhone: '', languages: ''
    });
    const [showSecondaryPhone, setShowSecondaryPhone] = useState(false);
    const { fetchUserData } = useContext(LoginContext);
    const [userLocation, setUserLocation] = useState({ lat: 0, lon: 0 });
    const [locationStatus, setLocationStatus] = useState('idle'); // 'idle' | 'loading' | 'success' | 'timeout'
    // let [loading, setLoading] = React.useState(false);

    const navigate = useNavigate();
    const nextStep = () => setStep(step + 1);
    const prevStep = () => step > 1 && setStep(step - 1);


    useEffect(() => {
        const fetchUserDetails = async () => {
            const token = localStorage.getItem("token");
            try {
                const response = await axios.get(`${process.env.REACT_APP_Backend_Host_Name}/api/v1/auth/checkLogin`, { headers: { Authorization: `Bearer ${token}` } });
                if (response.data.success && response.data.user) {
                    const user = response.data.user;
                    setFormData(prevData => ({
                        ...prevData,
                        fullName: user.fullName || '',
                        username: user.username?.includes('@') ? '' : user.username || '',
                        phone: user.phone || '',
                        secondaryPhone: user.secondaryPhone || '',
                        languages: user.languages || '',
                        country: user.buyerAddress?.country?.name ?
                            { value: user.buyerAddress.country.code, label: user.buyerAddress.country.name } : '',
                        state: user.buyerAddress?.state?.name ?
                            { value: user.buyerAddress.state.tax, label: user.buyerAddress.state.name } : '',
                        city: user.buyerAddress?.city || '',
                        address: user.buyerAddress?.address || '',
                        zipCode: user.buyerAddress?.zipCode || ''
                    }));
                    if (user.location && user.location.lat && user.location.lon) {
                        setUserLocation({ lat: user.location.lat, lon: user.location.lon });
                    }
                    // Show secondary phone input if user already has one
                    if (user.secondaryPhone) {
                        setShowSecondaryPhone(true);
                    }
                }
            } catch (error) {
                console.error('Error fetching user details', error);
                showToast('Failed to fetch user details', 'error');
            }
        };

        fetchUserDetails();
    }, [])

    // Fetch geolocation when reaching address step (step 2) if address not already set
    useEffect(() => {
        if (step === 2 && !formData.country && locationStatus === 'idle' && userLocation.lat === 0) {
            fetchGeoLocation();
        }
    }, [step]);

    const fetchGeoLocation = () => {
        if (!navigator.geolocation) return;
        setLocationStatus('loading');

        // After 3 seconds, stop showing loading so user can type manually
        const uiTimeout = setTimeout(() => {
            setLocationStatus(prev => prev === 'loading' ? 'timeout' : prev);
        }, 3000);

        navigator.geolocation.getCurrentPosition(
            async (position) => {
                const { latitude, longitude } = position.coords;
                setUserLocation({ lat: latitude, lon: longitude });
                try {
                    const res = await fetch(`${process.env.REACT_APP_Backend_Host_Name}/api/v1/auth/geocode?lat=${latitude}&lon=${longitude}`);
                    const data = await res.json();
                    if (data && data.address) {
                        const addr = data.address;
                        const countryName = addr.country;
                        const countryMatch = countries.find(c => c.name.toLowerCase() === countryName?.toLowerCase());
                        const stateName = addr.state || addr.region || '';
                        const cityName = addr.city || addr.town || addr.village || '';
                        const road = addr.road || addr.suburb || '';
                        const houseNumber = addr.house_number || '';
                        const fullAddress = [houseNumber, road].filter(Boolean).join(' ');
                        const zipCode = addr.postcode || '';

                        // Only fill fields that are still empty
                        setFormData(prev => ({
                            ...prev,
                            country: prev.country || (countryMatch ? { value: countryMatch.code, label: countryMatch.name } : prev.country),
                            state: prev.state || (stateName ? (countryMatch?.code === 'CA'
                                ? (() => { const s = states.find(s => s.name.toLowerCase() === stateName.toLowerCase()); return s ? { name: s.name, tax: s.tax } : { name: stateName, tax: 5 }; })()
                                : { name: stateName, tax: 5 }) : prev.state),
                            city: prev.city || cityName,
                            address: prev.address || fullAddress,
                            zipCode: prev.zipCode || zipCode
                        }));
                        clearTimeout(uiTimeout);
                        setLocationStatus('success');
                    } else {
                        clearTimeout(uiTimeout);
                        setLocationStatus('idle');
                    }
                } catch (err) {
                    console.log('Reverse geocoding failed:', err);
                    clearTimeout(uiTimeout);
                    setLocationStatus('idle');
                }
            },
            (error) => {
                console.log('Geolocation denied or failed:', error);
                clearTimeout(uiTimeout);
                setLocationStatus('idle');
            },
            { enableHighAccuracy: true, timeout: 10000 }
        );
    };

    const handleInputChange = (input, value) => {
        setFormData(prevData => {
            if (input === 'country') {
                return {
                    ...prevData,
                    country: value,
                    state: value.label === 'Canada' ? null : { name: '', tax: 5 }
                };
            }

            if (input === 'state' && formData.country?.label === 'Canada') {
                return {
                    ...prevData,
                    state: {
                        name: value.label,
                        tax: value.value
                    }
                };
            }

            return { ...prevData, [input]: value };
        });
    };


    const handleSubmit = (e) => {
        e.preventDefault();

        const currencyCode = getCurrencyForCountry(formData.country.value);
        const submissionData = {
            ...formData,
            country: {
                name: formData.country.label,
                code: formData.country.value
            },
            currency: currencyCode,
            location: userLocation.lat !== 0 ? userLocation : undefined
        };
        const token = localStorage.getItem("token");
        // setLoading(true);
        axios.post(`${process.env.REACT_APP_Backend_Host_Name}/api/v1/auth/updateUser`, submissionData, { headers: { Authorization: `Bearer ${token}` } }).then(() => {
            showToast("Profile completed successfully!", "success");
            fetchUserData();
            navigate("/")
        }).catch((e) => {
            console.log(e);
            showToast(e.response?.data?.error || "Something went wrong", "error")
        })
        // .finally(() => {
        // setLoading(false);
        // })
    };


    const renderPage = () => {
        switch (step) {

            case 1:
                return (
                    <div className="signUpSection">
                        <div className="rightSection">
                            <img src={thirdImage} alt="Food Dish" className="centerImage" />
                        </div>
                        <div className="leftSection">
                            <div className="signUpContent">
                                <div className="headerText">
                                    <h2 className="welcomeTitle">Complete your Profile</h2>
                                    <p className="topText">You are just 2 steps away!</p>
                                </div>
                                <form className='signUpForm' onSubmit={(e) => {
                                    e.preventDefault();
                                    if (!formData.fullName?.trim()) {
                                        showToast('Please enter your full name', 'error');
                                        return;
                                    }
                                    if (!formData.phone || formData.phone.length < 5) {
                                        showToast('Please enter a valid phone number', 'error');
                                        return;
                                    }
                                    if (!formData.languages) {
                                        showToast('Please select at least one language', 'error');
                                        return;
                                    }
                                    if (!formData.username?.trim()) {
                                        showToast('Please enter a username', 'error');
                                        return;
                                    }
                                    nextStep();
                                }}>
                                    <label htmlFor="fullName" className="label">Full Name</label>
                                    <input
                                        id="fullName"
                                        type="text"
                                        placeholder="Enter your full name"
                                        value={formData.fullName}
                                        onChange={(e) => handleInputChange('fullName', e.target.value)}
                                        required
                                        className='inputField'
                                    />
                                    <label htmlFor="username" className="label">Username</label>
                                    <input
                                        id="username"
                                        type="text"
                                        placeholder="Enter username"
                                        value={formData.username}
                                        onChange={(e) => handleInputChange('username', e.target.value)}
                                        required
                                        className='inputField'
                                    />
                                    <label htmlFor="phone" className="label">Phone Number</label>
                                    <PhoneInput
                                        country={'us'}
                                        value={formData.phone}
                                        onChange={(phone) => handleInputChange('phone', phone)}
                                        inputClass="phoneInputField"
                                        containerClass="phoneInputContainer"
                                        buttonClass="phoneInputButton"
                                        dropdownClass="phoneInputDropdown"
                                        enableSearch={true}
                                        searchPlaceholder="Search country..."
                                        inputProps={{
                                            required: true,
                                            id: 'phone'
                                        }}
                                    />

                                    {!showSecondaryPhone ? (
                                        <button
                                            type="button"
                                            className="addSecondaryPhoneBtn"
                                            onClick={() => setShowSecondaryPhone(true)}
                                        >
                                            <FaPlus className="addIcon" /> Add Secondary Phone Number
                                        </button>
                                    ) : (
                                        <>
                                            <label htmlFor="secondaryPhone" className="label">Secondary Phone Number (Optional)</label>
                                            <PhoneInput
                                                country={'us'}
                                                value={formData.secondaryPhone}
                                                onChange={(phone) => handleInputChange('secondaryPhone', phone)}
                                                inputClass="phoneInputField"
                                                containerClass="phoneInputContainer"
                                                buttonClass="phoneInputButton"
                                                dropdownClass="phoneInputDropdown"
                                                enableSearch={true}
                                                searchPlaceholder="Search country..."
                                                inputProps={{
                                                    id: 'secondaryPhone'
                                                }}
                                            />
                                        </>
                                    )}

                                    <label htmlFor="languages" className="label">Languages</label>
                                    <MultiSelect
                                        id="languages"
                                        options={languages}
                                        value={formData.languages?.split(', ') || []}
                                        onChange={(selectedLanguages) => handleInputChange('languages', selectedLanguages.map(lang => lang.label).join(', '))}
                                        placeholder="Select languages"
                                        required
                                    />

                                    <button type="submit" className="primaryBtn">Next</button>
                                </form>
                            </div>
                        </div>
                    </div>
                );

            case 2:
                return (
                    <div className="signUpSection">
                        <div className="leftSection">
                            <div className="signUpContent">
                                <div className="headerText">
                                    <h2 className="welcomeTitle">Enter your Address</h2>
                                    {locationStatus === 'loading' && (
                                        <div className="locationIndicator loading">
                                            <FaMapMarkerAlt className="pulsingIcon" /> Detecting your location...
                                        </div>
                                    )}
                                    {locationStatus === 'success' && (
                                        <div className="locationIndicator success">
                                            <FaCheck style={{ color: '#22c55e' }} /> Location detected — fields auto-filled
                                        </div>
                                    )}
                                    {locationStatus === 'timeout' && (
                                        <div className="locationIndicator timeout">
                                            <FaMapMarkerAlt style={{ color: '#f59e0b' }} /> Still detecting... You can fill in manually
                                        </div>
                                    )}
                                </div>
                                <form className='signUpForm' onSubmit={handleSubmit}>
                                    <div className="formRow2Col">
                                        <div className="formCol">
                                            <label htmlFor="country" className="label">Country</label>
                                            <Dropdown
                                                id="country"
                                                options={countries.map(country => ({ value: country.code, label: country.name }))}
                                                value={formData.country}
                                                onChange={(value) => handleInputChange('country', value)}
                                                placeholder='Select Country'
                                                required
                                            />
                                        </div>
                                        <div className="formCol">
                                            <label htmlFor="state" className="label">State/Province</label>
                                            {formData.country.label === "Canada" ?
                                                <Dropdown
                                                    id="state"
                                                    options={states.map(state => ({ value: state.tax, label: state.name }))}
                                                    value={formData.state ? { value: formData.state.tax, label: formData.state.name } : null}
                                                    onChange={(value) => handleInputChange('state', value)}
                                                    placeholder='Select State'
                                                    required
                                                /> :
                                                <input
                                                    id="state"
                                                    type="text"
                                                    placeholder="State/Province"
                                                    value={formData.state?.name || ''}
                                                    onChange={(e) => handleInputChange('state', { name: e.target.value, tax: 5 })}
                                                    required
                                                    className='inputField'
                                                />}
                                        </div>
                                    </div>
                                    <div className="formRow2Col">
                                        <div className="formCol">
                                            <label htmlFor="city" className="label">City</label>
                                            <input
                                                id="city"
                                                type="text"
                                                placeholder="City"
                                                value={formData.city}
                                                onChange={(e) => handleInputChange('city', e.target.value)}
                                                required
                                                className='inputField'
                                            />
                                        </div>
                                        <div className="formCol">
                                            <label htmlFor="zipCode" className="label">Zip/Postal Code</label>
                                            <input
                                                id="zipCode"
                                                type="text"
                                                placeholder="Zip/Postal Code"
                                                value={formData.zipCode}
                                                onChange={(e) => handleInputChange('zipCode', e.target.value)}
                                                className='inputField'
                                            />
                                        </div>
                                    </div>
                                    <label htmlFor="address" className="label">Address</label>
                                    <input
                                        id="address"
                                        type="text"
                                        placeholder="Street address"
                                        value={formData.address}
                                        onChange={(e) => handleInputChange('address', e.target.value)}
                                        required
                                        className='inputField'
                                    />
                                    <p className="noteText">
                                        Your location helps us find sellers near you
                                    </p>
                                    <div className="formButtonGroup">
                                        <button type="button" className="secondaryBtn backBtn" onClick={prevStep}>
                                            <FaArrowLeft /> Back
                                        </button>
                                        <button type="submit" className="primaryBtn nextBtn">Complete Profile</button>
                                    </div>
                                </form>
                            </div>
                        </div>
                        <div className="rightSection" style={{ backgroundImage: `url(${firstBackground})`, backgroundColor: 'transparent' }}>
                            <img key={step} src={secondImage} alt="Food Dish" className="centerImage" />
                        </div>
                    </div>
                );

            default:
                return null;
        }
    };


    return (
        <div className="signUpContainer">
            <SEO title="Complete profile" description="Complete your Chocowi profile." noIndex noFollow />
            {renderPage()}
        </div>
    );
};

export default SignUpForm;